import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { AppShell } from "../components/AppShell";
import { Icon } from "../components/Icon";
import {
  deleteAccountType,
  fetchCatalogSnapshot,
  saveAccountType,
} from "../lib/catalogApi";
import type { AccountCurrencyMode, AccountType, InstitutionKind } from "../lib/catalogTypes";
import { encodeInstitutionKinds, INSTITUTION_KINDS, parseInstitutionKindsJSON } from "../lib/catalogTypes";
import {
  encodeAllowedCurrencyCodes,
  foreignSelectableCurrencyCodes,
  parseAllowedCurrencyCodesJSON,
  SUPPORTED_CURRENCY_CODES,
} from "../lib/supportedCurrencies";

type Draft = Partial<AccountType> & {
  allowedCurrencyCodes?: string[];
  eligibleInstitutionKinds?: InstitutionKind[];
};

/** Kinds that may carry a currency template policy (mirrors the Worker's normalizer). */
function carriesCurrencyPolicy(mapsToKind: string | null | undefined): boolean {
  const kind = (mapsToKind ?? "").toLowerCase();
  return kind === "cash" || kind === "savings" || kind === "investment";
}

function resolveEditorCurrencyMode(
  mapsToKind: string | null | undefined,
  raw: AccountCurrencyMode | null | undefined,
): AccountCurrencyMode {
  if (!carriesCurrencyPolicy(mapsToKind)) return "none";
  const kind = (mapsToKind ?? "").toLowerCase();
  if (raw === "selectable" || raw === "fixed") return raw;
  if (raw === "none" && kind === "cash") return "none";
  if (kind === "investment") return "selectable";
  if (kind === "cash") return "none";
  return "fixed";
}

function currencyLabel(row: AccountType): string {
  if (row.currencyMode === "fixed") return row.fixedCurrencyCode ?? "—";
  if (row.currencyMode === "selectable") {
    const codes = parseAllowedCurrencyCodesJSON(row.allowedCurrencyCodesJSON);
    return codes.length ? codes.join(", ") : "Selectable";
  }
  return "—";
}

function institutionKindsLabel(row: AccountType): string {
  const kinds = parseInstitutionKindsJSON(row.eligibleInstitutionKindsJSON);
  return kinds.length ? kinds.join(", ") : "any";
}

export function AccountTypesPage() {
  const [rows, setRows] = useState<AccountType[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    const snap = await fetchCatalogSnapshot();
    setRows(snap.accountTypes);
  }, []);

  useEffect(() => {
    void reload().catch((e) => setError(e instanceof Error ? e.message : "Load failed"));
  }, [reload]);

  const mapsToInvestment = (editing?.mapsToKind ?? "").toLowerCase() === "investment";
  const mapsToCash = (editing?.mapsToKind ?? "").toLowerCase() === "cash";
  const hasCurrencyPolicy = carriesCurrencyPolicy(editing?.mapsToKind);
  const currencyMode = resolveEditorCurrencyMode(editing?.mapsToKind, editing?.currencyMode);

  const selectedAllowed = useMemo(
    () => editing?.allowedCurrencyCodes ?? parseAllowedCurrencyCodesJSON(editing?.allowedCurrencyCodesJSON),
    [editing?.allowedCurrencyCodes, editing?.allowedCurrencyCodesJSON],
  );

  const selectedKinds = useMemo(
    () => editing?.eligibleInstitutionKinds ?? parseInstitutionKindsJSON(editing?.eligibleInstitutionKindsJSON),
    [editing?.eligibleInstitutionKinds, editing?.eligibleInstitutionKindsJSON],
  );

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!editing?.slug || !editing.name) return;
    setBusy(true);
    setError(null);
    try {
      const payload = {
        recordName: editing.recordName,
        slug: editing.slug,
        name: editing.name,
        mapsToKind: editing.mapsToKind ?? null,
        currencyMode,
        fixedCurrencyCode: currencyMode === "fixed" ? editing.fixedCurrencyCode ?? "PHP" : null,
        allowedCurrencyCodesJSON:
          currencyMode === "selectable" ? encodeAllowedCurrencyCodes(selectedAllowed) : null,
        allowedCurrencyCodes: currencyMode === "selectable" ? selectedAllowed : [],
        eligibleInstitutionKindsJSON: encodeInstitutionKinds(selectedKinds),
        eligibleInstitutionKinds: selectedKinds,
        sortOrder: Number(editing.sortOrder ?? 0),
        isActive: editing.isActive !== false,
      };
      await saveAccountType(payload);
      setEditing(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  function startCreate() {
    setEditing({
      slug: "",
      name: "",
      mapsToKind: "savings",
      currencyMode: "fixed",
      fixedCurrencyCode: "PHP",
      allowedCurrencyCodes: foreignSelectableCurrencyCodes(),
      // Unrestricted by default: PH savings also live at government funds like Pag-IBIG MP2.
      eligibleInstitutionKinds: [],
      sortOrder: 0,
      isActive: true,
    });
  }

  function startEdit(row: AccountType) {
    setEditing({
      ...row,
      allowedCurrencyCodes: parseAllowedCurrencyCodesJSON(row.allowedCurrencyCodesJSON),
      eligibleInstitutionKinds: parseInstitutionKindsJSON(row.eligibleInstitutionKindsJSON),
    });
  }

  function toggleAllowed(code: string) {
    if (!editing) return;
    const next = new Set(selectedAllowed);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setEditing({ ...editing, allowedCurrencyCodes: Array.from(next) });
  }

  function toggleInstitutionKind(kind: InstitutionKind) {
    if (!editing) return;
    const next = new Set(selectedKinds);
    if (next.has(kind)) next.delete(kind);
    else next.add(kind);
    setEditing({ ...editing, eligibleInstitutionKinds: Array.from(next) });
  }

  return (
    <AppShell>
      <main className="page">
        <div className="page-header">
          <div>
            <span className="overline">Catalog</span>
            <h1>Account types</h1>
            <p>
              Templates for private cash, savings, and investment accounts. Currency and institution
              rules here are cosmetic — the app keeps each account’s real currency, balance, and
              holdings in private iCloud.
            </p>
          </div>
          <button className="btn btn-primary" type="button" onClick={startCreate}>
            <Icon name="plus" size={15} />
            Add account type
          </button>
        </div>
        {error && <div className="banner error">{error}</div>}
        {editing && (
          <form className="panel form-grid" onSubmit={(e) => void onSubmit(e)}>
            <label>
              Slug
              <input
                value={editing.slug ?? ""}
                onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                required
                disabled={Boolean(editing.recordName)}
              />
            </label>
            <label>
              Name
              <input
                value={editing.name ?? ""}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                required
              />
            </label>
            <label>
              mapsToKind
              <select
                value={editing.mapsToKind ?? ""}
                onChange={(e) => {
                  const mapsToKind = e.target.value || null;
                  const next: Draft = { ...editing, mapsToKind };
                  const kind = (mapsToKind ?? "").toLowerCase();
                  const isInvestment = kind === "investment";
                  const isCash = kind === "cash";
                  if (carriesCurrencyPolicy(mapsToKind)) {
                    if (isCash) {
                      next.currencyMode =
                        editing.currencyMode === "selectable" || editing.currencyMode === "fixed"
                          ? editing.currencyMode
                          : "none";
                    } else if (isInvestment) {
                      next.currencyMode = "selectable";
                    } else {
                      next.currencyMode =
                        editing.currencyMode === "selectable" ? "selectable" : "fixed";
                    }
                    next.fixedCurrencyCode =
                      next.currencyMode === "fixed" ? editing.fixedCurrencyCode ?? "PHP" : null;
                    next.allowedCurrencyCodes =
                      editing.allowedCurrencyCodes?.length
                        ? editing.allowedCurrencyCodes
                        : isInvestment
                          ? [...SUPPORTED_CURRENCY_CODES]
                          : foreignSelectableCurrencyCodes();
                  } else {
                    next.currencyMode = "none";
                    next.fixedCurrencyCode = null;
                    next.allowedCurrencyCodes = [];
                  }
                  if (isInvestment && !selectedKinds.length) {
                    next.eligibleInstitutionKinds = ["brokerage"];
                  }
                  setEditing(next);
                }}
              >
                <option value="">—</option>
                <option value="cash">cash</option>
                <option value="savings">savings</option>
                <option value="investment">investment</option>
                <option value="other">other</option>
              </select>
            </label>
            <label>
              Sort
              <input
                type="number"
                value={editing.sortOrder ?? 0}
                onChange={(e) => setEditing({ ...editing, sortOrder: Number(e.target.value) })}
              />
            </label>

            {hasCurrencyPolicy && (
              <>
                <label>
                  Currency mode
                  <select
                    value={currencyMode}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        currencyMode: e.target.value as AccountCurrencyMode,
                        fixedCurrencyCode:
                          e.target.value === "fixed"
                            ? editing.fixedCurrencyCode ?? "PHP"
                            : null,
                        allowedCurrencyCodes:
                          e.target.value === "selectable"
                            ? selectedAllowed.length
                              ? selectedAllowed
                              : foreignSelectableCurrencyCodes()
                            : [],
                      })
                    }
                  >
                    {mapsToCash && <option value="none">None (home currency)</option>}
                    <option value="fixed">Fixed currency</option>
                    <option value="selectable">Selectable currencies</option>
                  </select>
                </label>
                {currencyMode === "fixed" ? (
                  <label>
                    Currency
                    <select
                      value={editing.fixedCurrencyCode ?? "PHP"}
                      onChange={(e) =>
                        setEditing({ ...editing, fixedCurrencyCode: e.target.value })
                      }
                    >
                      {SUPPORTED_CURRENCY_CODES.map((code) => (
                        <option key={code} value={code}>
                          {code}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : currencyMode === "selectable" ? (
                  <div className="full">
                    <span className="field-hint">Allowed currencies</span>
                    <div className="chip-row" style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                      {SUPPORTED_CURRENCY_CODES.filter(
                        // Foreign cash/savings exclude home currency; investments may be peso-quoted.
                        (code) => code !== "PHP" || mapsToInvestment,
                      ).map((code) => {
                        const selected = selectedAllowed.includes(code);
                        return (
                          <button
                            key={code}
                            type="button"
                            className={selected ? "btn btn-brass" : "btn btn-secondary"}
                            onClick={() => toggleAllowed(code)}
                          >
                            {code}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <p className="muted full" style={{ margin: 0, fontSize: "0.85rem" }}>
                    Cash uses the person’s home currency (usually PHP). Switch to Selectable for
                    foreign cash wallets.
                  </p>
                )}
              </>
            )}

            <div className="full">
              <span className="field-hint">
                Eligible institution kinds — leave empty to offer every kind.
              </span>
              <div className="chip-row" style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                {INSTITUTION_KINDS.map((kind) => {
                  const selected = selectedKinds.includes(kind);
                  const labels: Record<string, string> = {
                    bank: "Bank",
                    brokerage: "Brokerage",
                    government: "Government",
                    insurer: "Insurer",
                    transportation: "Transportation",
                    other: "Other",
                  };
                  return (
                    <button
                      key={kind}
                      type="button"
                      className={selected ? "btn btn-brass" : "btn btn-secondary"}
                      onClick={() => toggleInstitutionKind(kind)}
                    >
                      {labels[kind] ?? kind}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="row-actions full">
              <button className="btn btn-primary" type="submit" disabled={busy}>
                Save
              </button>
              <button className="btn btn-secondary" type="button" onClick={() => setEditing(null)}>
                Cancel
              </button>
            </div>
          </form>
        )}
        <div className="panel table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>mapsToKind</th>
                <th>Currency</th>
                <th>Institution kinds</th>
                <th>Sort</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.recordName}>
                  <td>{row.name}</td>
                  <td>
                    <code>{row.slug}</code>
                  </td>
                  <td>
                    <code>{row.mapsToKind ?? "—"}</code>
                  </td>
                  <td>
                    <code>{currencyLabel(row)}</code>
                  </td>
                  <td>
                    <code>{institutionKindsLabel(row)}</code>
                  </td>
                  <td>{row.sortOrder}</td>
                  <td className="row-actions">
                    <button className="btn btn-secondary" type="button" onClick={() => startEdit(row)}>
                      Edit
                    </button>
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() =>
                        void deleteAccountType(row.recordName)
                          .then(reload)
                          .catch((err) =>
                            setError(err instanceof Error ? err.message : "Delete failed"),
                          )
                      }
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </AppShell>
  );
}
